// Catalog.ID Client — Public Profile Pane
// ============================================================================
// View-only pane showing any identity's public profile.
// Loaded via GET /api/profile/{cid}. Accessible both authenticated and unauthenticated.

import {
    createElement, createFieldGroup, createFormRow, createEmptyState, escapeHtml,
    renderPaneActions,
} from '@catalog-shell';
import type {
    PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
} from '@catalog-shell';
import { claimTypeLabel } from './claims-list-pane';
import * as profileApi from '../api/profile';
import * as trustApi from '../api/trust';
import { identity } from '../state/identity';
import type { PublicProfile } from '../api/types';
import { PANE_IDS } from '../shell-config';

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

// ----------------------------------------------------------------------------
// Title-row action
//
// Rating the identity on screen is a verb about the pane's own subject, so it
// rides in the title row (shell USAGE, "Where a button belongs"). It is offered
// in exactly one situation — a personal viewer looking at somebody else, once
// the trust fetch has landed — and its label says whether this would be a first
// rating or a revision. "Look Up" stays in the body: it commits the field above
// it, not the pane.
// ----------------------------------------------------------------------------

/** What the title row needs to know: whether rating is on offer, and prior say. */
export interface PublicProfileActionState {
    rate: { myRating: number | null } | null;
}

interface PublicProfileState extends PublicProfileActionState {
    ctx: PaneTypeRenderContext;
    cid: string | null;
}

const states = new WeakMap<PaneInstance, PublicProfileState>();

/** Pure: the rating verb, when it is on offer at all. */
export function planPublicProfileActions(
    st: PublicProfileActionState | undefined,
    exec: () => void,
): PaneActionDescriptor[] {
    if (!st?.rate) return [];
    return [{
        key: 'rate',
        label: st.rate.myRating === null ? 'Rate This Identity' : 'Update Your Rating',
        execute: () => exec(),
    }];
}

function publicProfileActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planPublicProfileActions(st, () => {
        if (!st?.cid) return;
        st.ctx.surface?.openPane({
            typeId: PANE_IDS.trustExpress,
            title: `Rate ${st.cid}`,
            context: { cid: st.cid },
        }, { originPaneId: st.ctx.instance.instanceId });
    }));
}

export function createPublicProfilePane(): PaneType {
    return {
        typeId: PANE_IDS.publicProfile,
        kind: 'leaf',
        defaultTitle: 'Public Profile',
        capabilities: [],
        tabBarActions: publicProfileActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const cid = ctx.instance.context['cid'] as string | undefined;
            const st: PublicProfileState = { ctx, cid: cid ?? null, rate: null };
            states.set(ctx.instance, st);

            if (!cid) {
                // Show a search form when no CID is provided
                renderSearchForm(container, ctx);
                return container;
            }

            // Load and display profile
            void renderProfile(container, cid, st);
            return container;
        },
    };
}

function renderSearchForm(container: HTMLElement, ctx: PaneTypeRenderContext): void {
    const group = createFieldGroup({ title: 'Look Up Identity' });

    const cidRow = createFormRow({ label: 'Catalog.ID' });
    const input = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    input.type = 'text';
    input.placeholder = 'Enter a Catalog.ID (e.g., abc1234efg)';
    cidRow.content.appendChild(input);
    group.body.appendChild(cidRow.element);

    const actionRow = createFormRow({ label: '' });
    const lookupBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Look Up',
    });
    lookupBtn.addEventListener('click', () => {
        const cid = input.value.trim();
        if (!cid) return;
        ctx.surface?.openPane({
            typeId: PANE_IDS.publicProfile,
            title: cid,
            context: { cid },
        }, { originPaneId: ctx.instance.instanceId });
    });
    actionRow.content.appendChild(lookupBtn);
    group.body.appendChild(actionRow.element);

    container.appendChild(group.element);
}

async function renderProfile(
    container: HTMLElement,
    cid: string,
    st: PublicProfileState,
): Promise<void> {
    const ctx = st.ctx;
    const loadingGroup = createFieldGroup({ title: 'Loading...' });
    container.appendChild(loadingGroup.element);

    let profile: PublicProfile;
    try {
        profile = await profileApi.getPublicProfile(cid);
    } catch {
        container.innerHTML = '';
        container.appendChild(createEmptyState({
            message: 'Profile Not Found',
            description: `No public profile exists for "${escapeHtml(cid)}".`,
        }));
        return;
    }

    container.innerHTML = '';

    // Identity info
    const infoGroup = createFieldGroup({ title: 'Identity' });

    const cidRow = createFormRow({ label: 'Catalog.ID' });
    cidRow.content.appendChild(createElement('span', {
        className: 'cid-profile-cid',
        textContent: profile.cid,
    }));
    infoGroup.body.appendChild(cidRow.element);

    const typeRow = createFormRow({ label: 'Type' });
    typeRow.content.appendChild(createElement('span', {
        textContent: ACCOUNT_TYPE_LABELS[profile.accountType] ?? profile.accountType,
    }));
    infoGroup.body.appendChild(typeRow.element);

    if (profile.terminated) {
        const statusRow = createFormRow({ label: 'Status' });
        const badge = createElement('span', {
            className: 'cid-terminated-badge',
            textContent: 'Terminated',
        });
        statusRow.content.appendChild(badge);
        infoGroup.body.appendChild(statusRow.element);
    }

    if (profile.organizationCid) {
        const orgRow = createFormRow({ label: 'Organization' });
        const orgLink = createElement('a', {
            className: 'cid-profile-link',
            textContent: profile.organizationCid,
        });
        orgLink.setAttribute('href', '#');
        orgLink.addEventListener('click', (e) => {
            e.preventDefault();
            ctx.surface?.openPane({
                typeId: PANE_IDS.publicProfile,
                title: profile.organizationCid!,
                context: { cid: profile.organizationCid },
            }, { originPaneId: ctx.instance.instanceId });
        });
        orgRow.content.appendChild(orgLink);
        infoGroup.body.appendChild(orgRow.element);
    }

    if (profile.principalCid) {
        const principalRow = createFormRow({ label: 'Principal' });
        const principalLink = createElement('a', {
            className: 'cid-profile-link',
            textContent: profile.principalCid,
        });
        principalLink.setAttribute('href', '#');
        principalLink.addEventListener('click', (e) => {
            e.preventDefault();
            ctx.surface?.openPane({
                typeId: PANE_IDS.publicProfile,
                title: profile.principalCid!,
                context: { cid: profile.principalCid },
            }, { originPaneId: ctx.instance.instanceId });
        });
        principalRow.content.appendChild(principalLink);
        infoGroup.body.appendChild(principalRow.element);
    }

    container.appendChild(infoGroup.element);

    // Trust score section (for active personal identities only — terminated accounts have no trust data)
    if (profile.accountType === 'natural_person' && !profile.terminated) {
        const trustGroup = createFieldGroup({ title: 'Trust Score' });
        const trustLoading = createElement('span', { className: 'cid-wizard-status', textContent: 'Loading...' });
        trustGroup.body.appendChild(trustLoading);
        container.appendChild(trustGroup.element);

        void trustApi.getTrustScore(cid).then(score => {
            trustGroup.body.removeChild(trustLoading);

            const scoreRow = createFormRow({ label: 'Global Score' });
            scoreRow.content.appendChild(createElement('span', {
                className: 'cid-trust-score-value',
                textContent: score.score.toFixed(4),
            }));
            trustGroup.body.appendChild(scoreRow.element);

            const tierRow = createFormRow({ label: 'Tier' });
            const tierBadge = createElement('span', {
                className: 'cid-trust-tier-badge',
                textContent: `Tier ${score.tier}`,
            });
            tierBadge.setAttribute('data-tier', String(score.tier));
            tierRow.content.appendChild(tierBadge);
            trustGroup.body.appendChild(tierRow.element);

            const inCountRow = createFormRow({ label: 'Incoming' });
            inCountRow.content.appendChild(createElement('span', { textContent: String(score.incomingCount) }));
            trustGroup.body.appendChild(inCountRow.element);

            const outCountRow = createFormRow({ label: 'Outgoing' });
            outCountRow.content.appendChild(createElement('span', { textContent: String(score.outgoingCount) }));
            trustGroup.body.appendChild(outCountRow.element);

            // Your rating — only shown to personal-identity viewers who have
            // already rated this target. The server populates `myRating` only
            // when the session is personal and has a trustexpression row for
            // this target (§5 keeps other raters' scores anonymous).
            if (typeof score.myRating === 'number') {
                const myRow = createFormRow({ label: 'Your Rating' });
                const myBadge = createElement('span', {
                    className: 'cid-my-rating-badge',
                    textContent: `${score.myRating}/5`,
                });
                myRow.content.appendChild(myBadge);
                trustGroup.body.appendChild(myRow.element);
            }

            // The rating verb (only for a personal viewer rating someone else)
            // lives in the title row, which was built before this fetch landed.
            const active = identity.getActive();
            if (active && active.accountType === 'natural_person' && active.cid !== cid) {
                st.rate = { myRating: typeof score.myRating === 'number' ? score.myRating : null };
                ctx.shell.refreshPaneActions(ctx.instance.instanceId);
            }
        }).catch(() => {
            trustGroup.body.removeChild(trustLoading);
            trustGroup.body.appendChild(createElement('span', {
                className: 'cid-wizard-status',
                textContent: 'Trust data unavailable',
            }));
        });
    }

    // Published claims
    if (profile.claims.length === 0) {
        const emptyGroup = createFieldGroup({ title: 'Published Claims' });
        emptyGroup.body.appendChild(createEmptyState({
            message: 'No public claims',
            description: 'This identity has no published claims.',
        }));
        container.appendChild(emptyGroup.element);
    } else {
        const claimsGroup = createFieldGroup({ title: 'Published Claims' });
        for (const claim of profile.claims) {
            const row = createFormRow({ label: claimTypeLabel(claim.claimType) });
            row.content.appendChild(createElement('span', {
                textContent: claim.publicValue,
            }));
            claimsGroup.body.appendChild(row.element);
        }
        container.appendChild(claimsGroup.element);
    }
}
